import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/solid';
import Button from '../../components/common/Button';
import ContactSalesModal from '../../components/modals/ContactSalesModal';
import { billingAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const billingIntervals = [
  { id: 'monthly', label: 'Monthly', discount: 0 },
  { id: 'quarterly', label: 'Quarterly', discount: 10 },
  { id: 'yearly', label: 'Yearly', discount: 20 },
];

const defaultFeatures = {
  starter: [
    '1 admin user',
    'NCSC task automation',
    'Basic security checks',
    'Simple evidence locker',
    'Microsoft OR Google (choose one)',
  ],
  pro: [
    'Up to 5 users',
    'All integrations (M365 + Google)',
    'Automated weekly checks',
    'Cyber Essentials readiness report',
    'Security alerts & risky users',
    'Task assignment',
    'Risk scoring dashboard',
  ],
  business: [
    'Up to 15 users',
    'Multi-site support',
    'Unlimited evidence storage',
    'Advanced workflow',
    'Alerts & reminders',
    'Priority support',
  ],
  enterprise: [
    'Unlimited users',
    'Unlimited client accounts',
    'White-label options',
    'API access',
    'Dedicated support',
    'Custom integrations',
  ],
};

export default function PricingPage() {
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [couponValid, setCouponValid] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [showContactSales, setShowContactSales] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await billingAPI.getPlans();
      // API client wraps response: { data: { success, data: { plans } } }
      const plans = response.data?.data?.plans || response.data?.plans || [];
      console.log('Fetched plans:', plans);
      setPlans(plans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      // Use default plans if API fails
      setPlans([
        { slug: 'starter', name: 'Starter', priceMonthly: 15, priceQuarterly: 40.50, priceYearly: 144, isPopular: false, description: 'For sole traders and micro-businesses' },
        { slug: 'pro', name: 'Pro', priceMonthly: 39, priceQuarterly: 105.30, priceYearly: 374.40, isPopular: true, description: 'For standard SMEs (5-50 employees)' },
        { slug: 'business', name: 'Business', priceMonthly: 79, priceQuarterly: 213.30, priceYearly: 758.40, isPopular: false, description: 'For larger SMEs and multi-site operations' },
        { slug: 'enterprise', name: 'Enterprise', priceMonthly: null, priceQuarterly: null, priceYearly: null, isPopular: false, description: 'For MSPs, IT providers, and accountants' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (plan) => {
    switch (billingInterval) {
      case 'yearly':
        return plan.priceYearly;
      case 'quarterly':
        return plan.priceQuarterly;
      default:
        return plan.priceMonthly;
    }
  };

  const getPriceDisplay = (plan) => {
    const price = getPrice(plan);
    if (price === null) return 'Custom';

    const monthly = parseFloat(plan.priceMonthly);
    let perMonth;

    switch (billingInterval) {
      case 'yearly':
        perMonth = parseFloat(price) / 12;
        break;
      case 'quarterly':
        perMonth = parseFloat(price) / 3;
        break;
      default:
        perMonth = parseFloat(price);
    }

    return `£${perMonth.toFixed(0)}`;
  };

  const getTotalPrice = (plan) => {
    const price = getPrice(plan);
    if (price === null) return null;
    return `£${parseFloat(price).toFixed(2)}`;
  };

  const getSavings = (plan) => {
    if (!plan.priceMonthly || billingInterval === 'monthly') return null;

    const monthly = parseFloat(plan.priceMonthly);
    const currentPrice = parseFloat(getPrice(plan));
    let normalPrice;

    switch (billingInterval) {
      case 'yearly':
        normalPrice = monthly * 12;
        break;
      case 'quarterly':
        normalPrice = monthly * 3;
        break;
      default:
        return null;
    }

    const savings = normalPrice - currentPrice;
    if (savings <= 0) return null;

    return `Save £${savings.toFixed(2)}`;
  };

  const getFeatures = (plan) => {
    return defaultFeatures[plan.slug] || [];
  };

  const handleSelectPlan = async (plan) => {
    if (plan.slug === 'enterprise') {
      setShowContactSales(true);
      return;
    }

    if (!user) {
      navigate(`/signup?plan=${plan.slug}&interval=${billingInterval}`);
      return;
    }

    // User is logged in, redirect to billing page to complete checkout
    // Use slug for URL as it's more reliable than id
    navigate(`/dashboard/billing?plan=${plan.slug}&interval=${billingInterval}`);
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    try {
      const { data } = await billingAPI.validateCoupon({ code: couponCode.trim() });
      if (data.valid) {
        setCouponValid(data.coupon);
        setCouponError('');
      } else {
        setCouponValid(null);
        setCouponError(data.reason || 'Invalid coupon code');
      }
    } catch (error) {
      setCouponValid(null);
      setCouponError('Failed to validate coupon');
    }
  };

  const currentInterval = billingIntervals.find(b => b.id === billingInterval);

  return (
    <div className="bg-white dark:bg-slate-900">
      {/* Header */}
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-5xl">
            Simple, Transparent Pricing
          </h1>
          <p className="mt-6 text-lg text-slate-600 dark:text-slate-300">
            Choose the plan that fits your organization. All plans include a 14-day free trial.
          </p>
        </div>

        {/* Billing Interval Toggle */}
        <div className="mt-10 flex justify-center">
          <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
            {billingIntervals.map((interval) => (
              <button
                key={interval.id}
                onClick={() => setBillingInterval(interval.id)}
                className={`relative rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  billingInterval === interval.id
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {interval.label}
                {interval.discount > 0 && (
                  <span className="ml-1.5 inline-flex items-center rounded-full bg-green-100 dark:bg-green-900 px-2 py-0.5 text-xs font-medium text-green-800 dark:text-green-200">
                    -{interval.discount}%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Coupon Code */}
        <div className="mt-6 flex justify-center">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder="Enter coupon code"
              className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400"
            />
            <Button variant="outline" size="sm" onClick={validateCoupon}>
              Apply
            </Button>
          </div>
        </div>
        {couponValid && (
          <p className="mt-2 text-center text-sm text-green-600 dark:text-green-400">
            Coupon applied: {couponValid.discountType === 'percent' ? `${couponValid.discountValue}% off` : `£${couponValid.discountValue} off`}
          </p>
        )}
        {couponError && (
          <p className="mt-2 text-center text-sm text-red-600 dark:text-red-400">{couponError}</p>
        )}
      </div>

      {/* Pricing Cards */}
      <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-8">
        {loading ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {plans.map((plan) => (
              <div
                key={plan.slug}
                className={`relative flex flex-col rounded-2xl p-8 shadow-sm border ${
                  plan.isPopular
                    ? 'border-primary-500 ring-2 ring-primary-500'
                    : 'border-slate-200 dark:border-slate-700'
                } bg-white dark:bg-slate-800`}
              >
                {plan.isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center rounded-full bg-primary-600 px-4 py-1 text-sm font-medium text-white">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{plan.name}</h3>
                  <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                    {plan.description}
                  </p>
                </div>

                <div className="mb-2">
                  {getPrice(plan) === null ? (
                    <span className="text-4xl font-bold text-slate-900 dark:text-white">Custom</span>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-slate-900 dark:text-white">
                        {getPriceDisplay(plan)}
                      </span>
                      <span className="text-slate-500 dark:text-slate-400">/month</span>
                    </>
                  )}
                </div>

                {billingInterval !== 'monthly' && getPrice(plan) !== null && (
                  <div className="mb-4 space-y-1">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {getTotalPrice(plan)} billed {billingInterval}
                    </p>
                    {getSavings(plan) && (
                      <p className="text-sm font-medium text-green-600 dark:text-green-400">
                        {getSavings(plan)}
                      </p>
                    )}
                  </div>
                )}

                <ul className="mb-8 space-y-3 flex-1">
                  {getFeatures(plan).map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <CheckIcon className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-600 dark:text-slate-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant={plan.isPopular ? 'primary' : 'outline'}
                  className="w-full"
                  onClick={() => handleSelectPlan(plan)}
                >
                  {plan.slug === 'enterprise' ? 'Contact Sales' : 'Start Free Trial'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FAQ Section */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="mx-auto mt-16 max-w-3xl">
            <dl className="space-y-8">
              {[
                {
                  question: 'Is there a free trial?',
                  answer: 'Yes! All plans include a 14-day free trial with full access to features. No credit card required to start.',
                },
                {
                  question: 'Can I change plans later?',
                  answer: 'Absolutely. You can upgrade or downgrade your plan at any time. Changes are prorated automatically.',
                },
                {
                  question: 'What payment methods do you accept?',
                  answer: 'We accept all major credit cards (Visa, Mastercard, American Express) securely processed by Stripe.',
                },
                {
                  question: 'Do you offer discounts for annual billing?',
                  answer: 'Yes! Save 10% with quarterly billing or 20% with annual billing compared to monthly plans.',
                },
                {
                  question: 'Is my data secure?',
                  answer: 'Security is our top priority. All data is encrypted at rest and in transit. We use read-only API access and maintain strict tenant isolation.',
                },
                {
                  question: 'Can I cancel anytime?',
                  answer: 'Yes, you can cancel your subscription at any time. You\'ll continue to have access until the end of your billing period.',
                },
              ].map((faq) => (
                <div key={faq.question}>
                  <dt className="text-lg font-semibold text-slate-900 dark:text-white">
                    {faq.question}
                  </dt>
                  <dd className="mt-2 text-slate-600 dark:text-slate-300">{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* Contact Sales Success Message */}
      {contactSuccess && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 shadow-lg">
            <p className="text-sm text-green-700 dark:text-green-300">{contactSuccess}</p>
            <button
              onClick={() => setContactSuccess(null)}
              className="mt-2 text-sm text-green-600 dark:text-green-400 hover:underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Contact Sales Modal */}
      <ContactSalesModal
        isOpen={showContactSales}
        onClose={() => setShowContactSales(false)}
        onSuccess={(message) => {
          setContactSuccess(message);
          setTimeout(() => setContactSuccess(null), 5000);
        }}
      />
    </div>
  );
}
